// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HardfinTabCardWrapper } from '@/components/hardfin-tab-card-wrapper';
import { Link } from '@/components/link';
import { ShippingCarrierTrackingLabel } from '@/components/shipping-carrier-tracking-label';
import { ArrowForward } from '@/constants/hardfin-standard-icons';
import { ShippingCarrier } from '@/constants/shipping-carrier';
import { Currency } from '@/currency/api/types';
import { formatCurrencyValue } from '@/currency/utilities/format';
import { usePurchaseOrder } from '@/purchase-order/hooks/use-purchase-order';
import { ShipmentDisplayStatus } from '@/shipment/api/types';
import { ShipmentQuantityCell } from '@/shipment/components/shipment-quantity-cell';
import { ShipmentStatusChip } from '@/shipment/components/shipment-status-chip';
import { useShipments } from '@/shipment/hooks/use-shipments';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

export const Route = createFileRoute(
  '/_app/purchase-orders/$purchaseOrderKey/shipments'
)({
  component: PurchaseOrderShipmentsTab,
});

function PurchaseOrderShipmentsTab() {
  const { purchaseOrderKey } = Route.useParams();
  const { purchaseOrder } = usePurchaseOrder(purchaseOrderKey);
  const { shipments } = useShipments({
    filter: { poNumbers: [purchaseOrder.purchaseOrderNumber] },
  });

  // Transform shipments into flat rows with product/part information
  // Only include shipment items that match PO line items
  const shipmentRows = useMemo(() => {
    const rows: {
      shipmentId: string;
      shipmentKey: string;
      shipmentNumber: string;
      status: ShipmentDisplayStatus;
      partSku: string;
      partName: string;
      quantity: number;
      receivedQuantity: number | null;
      quarantinedQuantity: number;
      shippedAt: DateTime | null;
      deliveredAt: DateTime | null;
      receivedAt: DateTime | null;
      carrier: ShippingCarrier | null;
      trackingNumber: string | null;
      shippingCost: Currency | null;
    }[] = [];

    // Build a set of item IDs from PO line items for quick lookup
    const poItemIds = new Set(purchaseOrder.items.map((item) => item.item.id));

    shipments.forEach((shipment) => {
      // Add a row for each item in the shipment that matches a PO line item
      shipment.items.forEach((item) => {
        const shouldInclude = poItemIds.has(item.id);
        const partSku = item.item.sku || '';
        const partName = item.item.name;

        // Only add the row if this shipment item matches a PO line item
        if (shouldInclude) {
          rows.push({
            shipmentId: shipment.id,
            shipmentKey: shipment.key,
            shipmentNumber: shipment.shipmentNumber,
            status: shipment.status,
            partSku,
            partName,
            quantity: item.quantity ?? 0,
            receivedQuantity: item.receivedQuantity,
            quarantinedQuantity: item.quarantinedQuantity,
            shippedAt: shipment.shippedAt,
            deliveredAt: shipment.deliveredAt,
            receivedAt: shipment.receivedAt,
            carrier: shipment.trackingNumberDetails?.carrier ?? null,
            trackingNumber:
              shipment.trackingNumberDetails?.trackingNumber ?? null,
            shippingCost: shipment.shippingCost,
          });
        }
      });
    });

    // Sort by ship date descending (most recent first), with shipment ID as secondary sort for stability
    rows.sort((a, b) => {
      const aDate = a.shippedAt?.toMillis() ?? 0;
      const bDate = b.shippedAt?.toMillis() ?? 0;

      // Sort by ship date descending
      if (bDate !== aDate) {
        return bDate - aDate;
      }

      // Secondary sort by shipment ID for stable sort
      return a.shipmentId.localeCompare(b.shipmentId);
    });

    return rows;
  }, [shipments, purchaseOrder.items]);

  return (
    <HardfinTabCardWrapper title="Shipments">
      <Box paddingY={2}>
        {shipmentRows.length === 0 ? (
          <Box padding={3}>
            <Typography variant="body2" color="text.secondary">
              No shipments
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Shipment ID</TableCell>
                  <TableCell>Part</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell align="right">Shipping cost</TableCell>
                  <TableCell>Ship date</TableCell>
                  <TableCell>Delivery date</TableCell>
                  <TableCell>Received date</TableCell>
                  <TableCell>Tracking</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipmentRows.map((row, index) => (
                  <TableRow key={`${row.shipmentId}-${index}`}>
                    <TableCell>
                      <ShipmentStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <Link
                        to="/shipments/$shipmentKey"
                        params={{ shipmentKey: row.shipmentKey }}
                        search={{ type: 'inbound' }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        {row.shipmentNumber}
                        <ArrowForward fontSize="small" />
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {row.partSku}{' '}
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.secondary"
                        >
                          ({row.partName})
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <ShipmentQuantityCell
                        plannedQuantity={row.quantity}
                        receivedQuantity={row.receivedQuantity}
                        isReceived={row.receivedAt !== null}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {row.shippingCost
                        ? formatCurrencyValue(row.shippingCost)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.shippedAt
                        ? row.shippedAt.toLocaleString({ dateStyle: 'medium' })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.deliveredAt
                        ? row.deliveredAt.toLocaleString({
                            dateStyle: 'medium',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {row.receivedAt
                        ? row.receivedAt.toLocaleString({
                            dateStyle: 'medium',
                          })
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <ShippingCarrierTrackingLabel
                        carrier={row.carrier}
                        trackingIdentifier={row.trackingNumber}
                        linkNumberDirectly={true}
                        variant="body2"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </HardfinTabCardWrapper>
  );
}
